/**
 * Schema.org LearningResource JSON-LD — wiki/knowledge-base/tutorial
 * pages. Signals EDUCATIONAL intent (vs generic Article), which AI
 * search engines use when grounding "How do I X?" queries.
 *
 * <p>For long-form tutorials that read like blog posts, emit BOTH
 * LearningResource and {@link ArticleSchemaJsonLd} on the same page —
 * the spec doesn't forbid coexistence.
 */
import { JsonLdScript } from './_shared';
import { buildPersonSchema, type PersonSchemaInput } from './PersonSchemaJsonLd';

export type LearningResourceType =
  | 'Tutorial'
  | 'Reference'
  | 'WikiPage'
  | 'Article'
  | 'Documentation'
  | 'CourseNotes'
  | 'Quiz'
  | string;

export type EducationalLevel = 'Beginner' | 'Intermediate' | 'Advanced' | 'AnyLevel' | string;

export interface LearningResourceSchemaJsonLdProps {
  name: string;
  description?: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
  inLanguage?: string;
  /** Default {@code 'Reference'}. */
  learningResourceType?: LearningResourceType;
  educationalLevel?: EducationalLevel;
  author?: PersonSchemaInput;
  publisherName: string;
  publisherLogoUrl?: string;
  keywords?: string[];
  /** ISO 8601 duration ("PT15M"). */
  timeRequired?: string;
  id?: string;
}

export function LearningResourceSchemaJsonLd({ id, ...input }: LearningResourceSchemaJsonLdProps) {
  const schema = buildLearningResourceSchema(input);
  return <JsonLdScript schema={schema} id={id ?? 'schema-learningresource'} />;
}

export function buildLearningResourceSchema(input: Omit<LearningResourceSchemaJsonLdProps, 'id'>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LearningResource',
    name: input.name,
    description: input.description,
    url: input.url,
    mainEntityOfPage: input.url,
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    inLanguage: input.inLanguage,
    learningResourceType: input.learningResourceType ?? 'Reference',
    educationalLevel: input.educationalLevel,
    timeRequired: input.timeRequired,
    author: input.author ? buildPersonSchema(input.author) : undefined,
    publisher: {
      '@type': 'Organization',
      name: input.publisherName,
      logo: input.publisherLogoUrl
        ? {
            '@type': 'ImageObject',
            url: input.publisherLogoUrl,
          }
        : undefined,
    },
    keywords: input.keywords && input.keywords.length > 0 ? input.keywords.join(', ') : undefined,
  };
}
